/**
 * LandlordCardDe — German variant of LandlordCard.
 *
 * Heyflow widget tag:
 *   <div data-conny-widget="landlord-card-de"></div>
 *
 * Reads the same sessionStorage keys as LandlordCard.
 * Storage values (conny_landlord_raw, conny_landlord_type, etc.) are kept
 * in English so existing Heyflow funnels and the backend are unaffected.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { getFunnelContext, listLandlords, Landlord } from "../../shared/api";
import { readAddress } from "../../shared/address";
import { CheckIcon, PinIcon } from "../../shared/icons";
import {
  removeStorage,
  STORAGE_KEYS,
  useStorage,
  writeStorage,
} from "../../shared/storage";

interface Selection {
  category: "" | "private" | "commercial" | "not_sure";
  knownKey: string;
  raw: string;
  type: string;
  userSelected: boolean;
}

function saveSelection(
  category: Selection["category"],
  knownKey: string,
  userSelected: boolean,
  byKey: Record<string, Landlord>,
  addressKey: string = "",
) {
  let raw = "";
  let type = "";
  let key = knownKey;

  if (category === "private") {
    raw = "Private landlord";
    type = "private";
    key = "";
  } else if (category === "commercial") {
    if (key && byKey[key]) {
      raw = byKey[key].name;
      type = byKey[key].type || "other_corporate";
    } else if (key === "other_commercial") {
      raw = "Other commercial landlord";
      type = "other_corporate";
    } else {
      key = "";
      raw = "";
      type = "other_corporate";
    }
  } else if (category === "not_sure") {
    raw = "Not sure";
    type = "";
    key = "";
  }

  writeStorage(STORAGE_KEYS.landlordCategory, category);
  writeStorage(STORAGE_KEYS.landlordKnownKey, key);
  writeStorage(STORAGE_KEYS.landlordRaw, raw);
  writeStorage(STORAGE_KEYS.landlordType, type);
  writeStorage("landlord", raw);
  writeStorage("landlord_type", type);
  writeStorage("conny_landlord", raw);
  writeStorage("conny_landlord_name", raw);

  if (userSelected) {
    writeStorage(STORAGE_KEYS.landlordUserSelected, "true");
    removeStorage(STORAGE_KEYS.landlordAutoSelected);
  } else {
    writeStorage(STORAGE_KEYS.landlordAutoSelected, "true");
  }
  writeStorage(STORAGE_KEYS.landlordSelectedForAddress, addressKey);

  window.dispatchEvent(
    new CustomEvent("conny-landlord-selection-updated", {
      detail: {
        landlord: raw,
        landlordType: type,
        category,
        knownKey: key,
        userSelected,
      },
    }),
  );
}

function useReactiveAddress() {
  useStorage(STORAGE_KEYS.street);
  useStorage(STORAGE_KEYS.house);
  useStorage(STORAGE_KEYS.zip);
  useStorage(STORAGE_KEYS.address);
  return readAddress();
}

export function LandlordCardDe() {
  const [category] = useStorage(STORAGE_KEYS.landlordCategory);
  const [knownKey] = useStorage(STORAGE_KEYS.landlordKnownKey);
  const [userSelectedStr] = useStorage(STORAGE_KEYS.landlordUserSelected);
  const [autoSelectedStr] = useStorage(STORAGE_KEYS.landlordAutoSelected);
  const [selectedForAddress] = useStorage(
    STORAGE_KEYS.landlordSelectedForAddress,
  );
  const userSelected = userSelectedStr === "true";
  void autoSelectedStr;
  const addr = useReactiveAddress();
  const addressKey = `${addr.street}|${addr.houseNumber}|${addr.zip}`;

  const landlordsQuery = useQuery({
    queryKey: ["landlords"],
    queryFn: () => listLandlords(),
    staleTime: 30 * 60_000,
  });
  const landlords = landlordsQuery.data ?? [];

  const byKey = useMemo<Record<string, Landlord>>(() => {
    const m: Record<string, Landlord> = {};
    for (const l of landlords) m[l.key] = l;
    return m;
  }, [landlords]);

  const ctxQuery = useQuery({
    queryKey: ["funnel-context", addr.street, addr.houseNumber, addr.zip],
    queryFn: () =>
      getFunnelContext({
        street: addr.street,
        house: addr.houseNumber,
        zip: addr.zip,
      }),
    enabled: Boolean(addr.street && addr.houseNumber && addr.zip),
    staleTime: 5 * 60_000,
  });
  const detected = ctxQuery.data?.landlord ?? null;

  const byKeyWithDetected = useMemo<Record<string, Landlord>>(() => {
    if (!detected) return byKey;
    return { ...byKey, [detected.key]: detected };
  }, [byKey, detected]);

  useEffect(() => {
    if (!detected) return;
    const committedForThisAddress = selectedForAddress === addressKey;
    if (committedForThisAddress) return;
    if (knownKey === detected.key && category === "commercial") return;
    saveSelection(
      "commercial",
      detected.key,
      false,
      byKeyWithDetected,
      addressKey,
    );
  }, [
    detected,
    knownKey,
    category,
    byKeyWithDetected,
    selectedForAddress,
    addressKey,
  ]);

  const selection: Selection = {
    category: (category as Selection["category"]) || "",
    knownKey,
    raw: "",
    type: "",
    userSelected,
  };

  const detectedSelected =
    Boolean(detected) &&
    selection.category === "commercial" &&
    selection.knownKey === detected!.key;
  const showCommercialPanel =
    selection.category === "commercial" && !detectedSelected;

  return (
    <div className="cn">
      <div className="cn-card cn-select">
        {detected && (
          <DetectedOption
            landlord={detected}
            selected={detectedSelected}
            onClick={() =>
              saveSelection(
                "commercial",
                detected.key,
                true,
                byKeyWithDetected,
                addressKey,
              )
            }
          />
        )}

        <div className="cn-options" role="radiogroup" aria-label="Vermietertyp">
          <Option
            title="Privater Vermieter"
            copy="Eine Privatperson vermietet die Wohnung."
            selected={selection.category === "private"}
            onClick={() =>
              saveSelection("private", "", true, byKey, addressKey)
            }
          />
          <Option
            title="Wohnungsunternehmen"
            copy="Ihr Vermieter ist ein gewerbliches Wohnungsunternehmen."
            selected={selection.category === "commercial" && !detectedSelected}
            onClick={() => {
              const preselect = detected ? "" : selection.knownKey || "";
              saveSelection(
                "commercial",
                preselect,
                true,
                byKeyWithDetected,
                addressKey,
              );
            }}
          />

          {showCommercialPanel && (
            <div className="cn-sub-panel">
              <div className="cn-sub-panel-title">
                Wie heißt das Unternehmen?
              </div>
              <div
                className="cn-options"
                role="radiogroup"
                aria-label="Wohnungsunternehmen"
              >
                {(() => {
                  const list = landlords
                    .filter((l) => l.key !== "other_commercial")
                    .slice();
                  if (detected && !list.some((l) => l.key === detected.key)) {
                    list.push(detected);
                  }
                  list.sort((a, b) => {
                    if (detected) {
                      if (a.key === detected.key) return -1;
                      if (b.key === detected.key) return 1;
                    }
                    return (b.count || 0) - (a.count || 0);
                  });
                  return list.map((l) => (
                    <Option
                      key={l.key}
                      title={l.name}
                      copy={
                        l.count
                          ? `${l.count.toLocaleString("de-DE")} Fälle bisher`
                          : "Bekanntes Wohnungsunternehmen"
                      }
                      selected={selection.knownKey === l.key}
                      onClick={() =>
                        saveSelection(
                          "commercial",
                          l.key,
                          true,
                          byKeyWithDetected,
                          addressKey,
                        )
                      }
                      variant="sub"
                      autoDetected={
                        Boolean(detected) && l.key === detected!.key
                      }
                    />
                  ));
                })()}
                <Option
                  title="Anderes Wohnungsunternehmen"
                  copy="Ein anderes Wohnungsunternehmen."
                  selected={selection.knownKey === "other_commercial"}
                  onClick={() =>
                    saveSelection(
                      "commercial",
                      "other_commercial",
                      true,
                      byKeyWithDetected,
                      addressKey,
                    )
                  }
                  variant="sub"
                />
              </div>
            </div>
          )}

          <Option
            title="Nicht sicher"
            copy="Kein Problem — wir können Ihren Fall trotzdem prüfen."
            selected={selection.category === "not_sure"}
            onClick={() =>
              saveSelection("not_sure", "", true, byKey, addressKey)
            }
          />
        </div>

        <div className="cn-reassurance">
          <span className="cn-row-left">
            <span className="cn-check">
              <CheckIcon />
            </span>
            <span className="cn-reassurance-text">
              Conny kontaktiert Ihren Vermieter erst, nachdem Sie uns per E-Mail
              beauftragt haben.
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

function Option({
  title,
  copy,
  selected,
  onClick,
  variant = "main",
  autoDetected = false,
}: {
  title: string;
  copy: string;
  selected: boolean;
  onClick: () => void;
  variant?: "main" | "sub";
  autoDetected?: boolean;
}) {
  const cls = variant === "sub" ? "cn-option--sub" : "cn-option";
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={`${cls} ${selected ? "is-selected" : ""}`}
      onClick={onClick}
    >
      <span className="cn-radio" aria-hidden="true" />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="cn-opt-title">
          {title}
          {autoDetected && <span className="cn-auto-badge">Erkannt</span>}
        </span>
        <span className="cn-opt-copy">{copy}</span>
      </span>
    </button>
  );
}

function DetectedOption({
  landlord,
  selected,
  onClick,
}: {
  landlord: Landlord;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={`cn-detected ${selected ? "is-selected" : ""}`}
      onClick={onClick}
    >
      <span className="cn-radio" aria-hidden="true" />
      <span style={{ flex: 1, minWidth: 0 }}>
        <div className="cn-detected-top">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cn-detected-label">
              <PinIcon /> <span>Vermieter erkannt</span>
            </div>
            <div className="cn-detected-name">{landlord.name}</div>
          </div>
          <span className="cn-chip">
            {landlord.count
              ? `${landlord.count.toLocaleString("de-DE")} Fälle bisher`
              : "Erkannt"}
          </span>
        </div>
        <div className="cn-detected-copy">
          {landlord.count
            ? `Gute Nachrichten — Conny hat bereits ${landlord.count.toLocaleString("de-DE")} Fälle gegen ${landlord.name} bearbeitet. Wenn das Ihr Vermieter ist, lassen Sie die Auswahl bestehen.`
            : `Basierend auf Ihrer Adresse haben wir ${landlord.name} als Ihren Vermieter identifiziert. Wenn das korrekt ist, lassen Sie die Auswahl bestehen.`}
        </div>
      </span>
    </button>
  );
}
